import React, { memo, useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FixedSizeList as List } from "react-window";
import SelectedValuesChips from "../ui-components/SelectedValuesChips.jsx";

const API_URL = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 100;
const LINE_HEIGHT = 36;
const MAX_LIST_HEIGHT = 500;
const SCROLL_THRESHOLD = 0.85;
const SEARCH_DEBOUNCE_MS = 300;
const HEADER_HEIGHT = 140; // Height of the AdministrationHeader

// Memoize static icons
const closeIcon = icon({ name: "chevrons-right", family: "sharp", style: "light" });
const searchIcon = icon({ name: "magnifying-glass", family: "sharp", style: "light" });
const clearIcon = icon({ name: "xmark", family: "sharp", style: "regular" });

/**
 * FilterSidebar - Sidebar for selecting filter values
 * 
 * Matches the design patterns of WidgetConfigSidebar and ReportSidebar.
 * Includes search, selected values chips, virtualized options list,
 * include/exclude mode selector, and remove filter button.
 */
const FilterSidebar = memo(({
	isOpen,
	onClose,
	filter,
	onUpdateFilter,
	onRemoveFilter,
	token,
	additionalFilters,
	excludeCategories,
	headerHeight = HEADER_HEIGHT,
	headerRef,
}) => {
	// Local state
	const [searchValue, setSearchValue] = useState('');
	const [options, setOptions] = useState([]);
	const [pagination, setPagination] = useState({ offset: 0, hasMore: true, loading: false });
	const [isSearching, setIsSearching] = useState(false);
	const [filterMode, setFilterMode] = useState('include'); // 'include' or 'exclude'
	
	// Internal state for animation - starts false, then transitions to true when isOpen becomes true
	const [isVisible, setIsVisible] = useState(false);

	// Refs
	const abortControllerRef = useRef(null);
	const searchTimerRef = useRef(null);
	const listRef = useRef(null);
	const listOuterRef = useRef(null);
	const scrollPositionRef = useRef(0);
	const prevOptionsLengthRef = useRef(0);
	const hasUserScrolledRef = useRef(false);
	const paginationRef = useRef(pagination);
	const optionsLengthRef = useRef(options.length);
	
	// Keep refs in sync with state
	paginationRef.current = pagination;
	optionsLengthRef.current = options.length;
	
	// Ref to the sidebar element for direct DOM manipulation (avoids React state lag)
	const sidebarRef = useRef(null);
	
	// Handle opening animation - delay setting isVisible to true to allow initial closed state to render
	useEffect(() => {
		if (isOpen) {
			// Use requestAnimationFrame to ensure the initial closed state is rendered first
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setIsVisible(true);
				});
			});
		} else {
			setIsVisible(false);
		}
	}, [isOpen]);

	// Track the header element's position and update sidebar directly (no React state = no lag)
	useLayoutEffect(() => {
		if (!isOpen) return;
		
		const sidebar = sidebarRef.current;
		if (!sidebar) return;
		
		// Find the header element once using stable selector or ref
		const header = headerRef?.current || document.querySelector('[data-header="administration"]');
		
		const updatePosition = () => {
			// Calculate top position based on header's actual position
			const top = header
				? Math.max(0, header.getBoundingClientRect().bottom)
				: Math.max(0, headerHeight - (window.scrollY || 0));
			
			// Direct DOM update - no React re-render, no lag
			sidebar.style.top = `${top}px`;
		};
		
		// Initial position
		updatePosition();
		
		// Update on scroll - use single document-level listener with capture for all scroll events
		const handleScroll = () => {
			updatePosition();
		};
		
		// Single document-level scroll listener with capture to catch all scroll events
		document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
		
		return () => {
			document.removeEventListener('scroll', handleScroll, { capture: true });
		};
	}, [isOpen, headerHeight, headerRef]);

	// Get selected values from filter
	const selectedValues = useMemo(() => {
		return filter?.selectedValues || [];
	}, [filter?.selectedValues]);

	// Create a Set for O(1) lookups
	const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

	// Fetch filter values
	const fetchFilterValues = useCallback((offset = 0, append = false, searchTerm = '') => {
		if (!token || !filter?.category) return;

		// Cancel any existing request
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		const controller = new AbortController();
		abortControllerRef.current = controller;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token);

		const requestBody = {
			search_term: searchTerm,
			category: filter.category,
			filters: additionalFilters,
			exclude_categories: excludeCategories,
			offset: offset,
			limit: PAGE_SIZE,
		};

		setPagination(prev => ({ ...prev, loading: true }));
		// Only show "Searching..." for new searches, not for pagination (append)
		if (!append && searchTerm !== undefined) {
			setIsSearching(true);
		}

		fetch(API_URL + `/get-search-terms-v2`, {
			method: "POST",
			headers: headers,
			body: JSON.stringify(requestBody),
			signal: controller.signal,
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error("Invalid response code: " + response.status);
				}
				return response.json();
			})
			.then((json) => {
				const newOptions = (json.options || [])
					.map((item) => {
						const label = item.term || item.term_display || item.label || "";
						return {
							label: label,
							value: item.term || item.value || label,
						};
					})
					.filter((item) => item.label);

				const hasMore = json.hasMore || false;
				const newOffset = offset + newOptions.length;

				setPagination({
					offset: newOffset,
					hasMore: hasMore,
					loading: false,
				});
				setIsSearching(false);

				setOptions(prev => append ? [...prev, ...newOptions] : newOptions);
			})
			.catch((err) => {
				if (err.name === 'AbortError') return;
				console.error("Failed to fetch filter values:", err);
				setPagination(prev => ({ ...prev, loading: false }));
				setIsSearching(false);
			});
	}, [token, filter?.category, additionalFilters, excludeCategories]);

	// Load initial values when sidebar opens
	useEffect(() => {
		if (isOpen && filter?.category) {
			setSearchValue('');
			setOptions([]);
			setPagination({ offset: 0, hasMore: true, loading: false });
			// Reset scroll tracking refs
			scrollPositionRef.current = 0;
			prevOptionsLengthRef.current = 0;
			hasUserScrolledRef.current = false;
			fetchFilterValues(0, false, '');
		}

		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			if (searchTimerRef.current) {
				clearTimeout(searchTimerRef.current);
			}
		};
	}, [isOpen, filter?.category, fetchFilterValues]);

	// Handle search with debouncing
	const handleSearchChange = useCallback((e) => {
		const value = e.target.value;
		setSearchValue(value);

		if (searchTimerRef.current) {
			clearTimeout(searchTimerRef.current);
		}

		searchTimerRef.current = setTimeout(() => {
			setPagination({ offset: 0, hasMore: true, loading: false });
			// Reset scroll tracking for new search
			scrollPositionRef.current = 0;
			prevOptionsLengthRef.current = 0;
			hasUserScrolledRef.current = false;
			fetchFilterValues(0, false, value);
		}, SEARCH_DEBOUNCE_MS);
	}, [fetchFilterValues]);

	// Clear search
	const handleClearSearch = useCallback(() => {
		setSearchValue('');
		setPagination({ offset: 0, hasMore: true, loading: false });
		// Reset scroll tracking
		scrollPositionRef.current = 0;
		prevOptionsLengthRef.current = 0;
		hasUserScrolledRef.current = false;
		fetchFilterValues(0, false, '');
	}, [fetchFilterValues]);

	// Clear all selections
	const handleClearAll = useCallback(() => {
		onUpdateFilter?.(filter?.category, []);
	}, [onUpdateFilter, filter?.category]);

	// Load more options (infinite scroll) - uses refs to avoid callback recreation
	const loadMore = useCallback(() => {
		const pag = paginationRef.current;
		if (pag.loading || !pag.hasMore) return;
		fetchFilterValues(pag.offset, true, searchValue);
	}, [searchValue, fetchFilterValues]);

	// Handle scroll for infinite loading - stable callback using refs
	const handleScroll = useCallback(({ scrollOffset, scrollUpdateWasRequested }) => {
		// Always track scroll position
		scrollPositionRef.current = scrollOffset;
		
		// Mark that user has scrolled (for scroll restoration logic)
		if (!scrollUpdateWasRequested && scrollOffset > 0) {
			hasUserScrolledRef.current = true;
		}

		if (scrollUpdateWasRequested) return;
		
		const pag = paginationRef.current;
		if (pag.loading || !pag.hasMore) return;

		const currentOptionsLength = optionsLengthRef.current;
		const totalHeight = currentOptionsLength * LINE_HEIGHT;
		const viewHeight = Math.min(currentOptionsLength * LINE_HEIGHT, MAX_LIST_HEIGHT);
		const scrolledRatio = (scrollOffset + viewHeight) / totalHeight;

		if (scrolledRatio >= SCROLL_THRESHOLD) {
			loadMore();
		}
	}, [loadMore]);
	
	// Restore scroll position after new items are loaded (pagination)
	useEffect(() => {
		const prevLength = prevOptionsLengthRef.current;
		const currentLength = options.length;
		
		// If items were added (pagination), restore scroll position
		if (currentLength > prevLength && prevLength > 0 && hasUserScrolledRef.current) {
			const savedPosition = scrollPositionRef.current;
			
			// Use requestAnimationFrame to ensure DOM is updated, then restore scroll
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					// Try using the outer ref (DOM element) first for more reliable scroll
					if (listOuterRef.current && savedPosition > 0) {
						listOuterRef.current.scrollTop = savedPosition;
					} else if (listRef.current && savedPosition > 0) {
						listRef.current.scrollTo(savedPosition);
					}
				});
			});
		}
		
		prevOptionsLengthRef.current = currentLength;
	}, [options.length]);

	// Toggle option selection
	const toggleOption = useCallback((optionLabel) => {
		const newSelection = selectedSet.has(optionLabel)
			? selectedValues.filter(v => v !== optionLabel)
			: [...selectedValues, optionLabel];
		onUpdateFilter?.(filter?.category, newSelection);
	}, [selectedSet, selectedValues, onUpdateFilter, filter?.category]);

	// Remove a selected value
	const handleRemoveSelected = useCallback((value) => {
		const newSelection = selectedValues.filter(v => v !== value);
		onUpdateFilter?.(filter?.category, newSelection);
	}, [selectedValues, onUpdateFilter, filter?.category]);

	// Handle remove filter
	const handleRemoveFilter = useCallback(() => {
		onRemoveFilter?.(filter?.category);
		onClose?.();
	}, [onRemoveFilter, filter?.category, onClose]);

	// Handle escape key
	useEffect(() => {
		const handleEscape = (e) => {
			if (e.key === 'Escape' && isOpen) {
				onClose?.();
			}
		};
		document.addEventListener('keydown', handleEscape);
		return () => document.removeEventListener('keydown', handleEscape);
	}, [isOpen, onClose]);

	// Handle click outside to close sidebar
	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (e) => {
			const sidebar = sidebarRef.current;
			if (sidebar && !sidebar.contains(e.target)) {
				// Also check if click was on a constraint chip (which opens the sidebar)
				const isChipClick = e.target.closest('[data-constraint-chip]');
				if (!isChipClick) {
					onClose?.();
				}
			}
		};

		// Use mousedown for immediate response
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen, onClose]);

	// Calculate list height - use available space dynamically
	const listHeight = useMemo(() => {
		// Calculate based on number of options, with a reasonable max
		const calculatedHeight = options.length * LINE_HEIGHT;
		// Cap at 500px to leave room for other elements
		return Math.min(calculatedHeight, 500) || LINE_HEIGHT;
	}, [options.length]);

	// Highlight matching text in search results
	const highlightMatch = useCallback((text, searchQuery) => {
		if (!searchQuery || !text) return text;
		
		const query = searchQuery.trim().toLowerCase();
		if (!query) return text;
		
		const textLower = text.toLowerCase();
		const matchIndex = textLower.indexOf(query);
		
		if (matchIndex === -1) return text;
		
		const before = text.slice(0, matchIndex);
		const match = text.slice(matchIndex, matchIndex + query.length);
		const after = text.slice(matchIndex + query.length);
		
		return (
			<>
				{before}
				<span className="font-semibold text-text">{match}</span>
				{after}
			</>
		);
	}, []);

	// Row renderer for virtualized list
	const Row = useCallback(({ index, style }) => {
		const option = options[index];
		if (!option) return null;

		const isSelected = selectedSet.has(option.label);

		return (
			<div style={style} className="px-4">
				<label className="flex items-center py-2 cursor-pointer hover:bg-surface-2 rounded px-2 -mx-2">
					<input
						type="checkbox"
						checked={isSelected}
						onChange={() => toggleOption(option.label)}
						className="mr-3 cursor-pointer accent-primary"
					/>
					<span
						className="text-sm text-text truncate"
						title={option.label}
					>
						{highlightMatch(option.label, searchValue)}
					</span>
				</label>
			</div>
		);
	}, [options, selectedSet, toggleOption, highlightMatch, searchValue]);

	// Use Portal to render sidebar at document body level to avoid CSS containment issues
	const sidebarContent = (
		<div
			ref={sidebarRef}
			className={`fixed bg-card border-l border-card-bd ${
				isVisible ? '' : 'pointer-events-none'
			}`}
			style={{
				right: 0,
				top: `${headerHeight}px`,
				bottom: 0,
				width: '420px',
				zIndex: 9999,
				transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
				opacity: isVisible ? 1 : 0,
				transition: 'transform 300ms ease-in-out, opacity 300ms ease-in-out',
			}}
			data-filter-sidebar
		>
			{filter && (
			<div className="flex flex-col h-full">
				{/* Header */}
				<div className="flex items-center justify-between px-4 py-1 border-b border-card-bd flex-shrink-0">
					<h3 className="text-sm font-medium text-text capitalize">
						{filter.categoryDisplay || filter.category}
						<span className="text-xs text-text-3 pl-2"> refining filter</span>
					</h3>
					<button
						type="button"
						onClick={onClose}
						className="p-1 rounded hover:bg-surface-2 transition-colors cursor-pointer"
						aria-label="Close sidebar"
					>
						<FontAwesomeIcon
							icon={closeIcon}
							className="text-text-2 text-xs"
						/>
					</button>
				</div>

				{/* Scrollable content area */}
				<div className="flex-1 overflow-y-auto min-h-0">
					{/* Search input */}
					<div className="px-4 py-3 border-b border-card-bd">
						<div className="flex items-center gap-2">
							<div className="flex-1 relative">
								<FontAwesomeIcon
									icon={searchIcon}
									className="absolute left-3 top-1/2 -translate-y-1/2 text-text-2 text-sm"
								/>
								<input
									type="text"
									value={searchValue}
									onChange={handleSearchChange}
									placeholder="Search values..."
									className="w-full pl-9 pr-3 py-2 text-text-2 text-sm border border-border rounded-ctrl focus:outline-none focus:ring-primary bg-surface-2"
								/>
							</div>
						</div>
					</div>

					{/* Selected values */}
					{selectedValues.length > 0 && (
						<div className="px-4 py-3 border-b border-card-bd">
							<div className="flex items-center justify-between mb-2">
								<div className="text-sm text-text-2">
									{selectedValues.length} selected
								</div>
								<button
									type="button"
									onClick={handleClearAll}
									className="text-sm text-text-2 hover:text-text whitespace-nowrap cursor-pointer"
									aria-label="Clear all selections"
								>
									Clear
								</button>
							</div>
							<SelectedValuesChips
								values={selectedValues}
								onRemove={handleRemoveSelected}
								maxVisible={5}
							/>
						</div>
					)}

					{/* Available options header */}
					<div className="px-4 py-2">
						<div className="flex items-center justify-between">
							<span className="text-sm text-text-2">
								Available options
							</span>
							{searchValue && (
								<span className="text-xs text-warning flex items-center gap-1">
									<span>Filtered</span>
									<button
										type="button"
										onClick={handleClearSearch}
										className="hover:text-warning cursor-pointer"
										aria-label="Clear search filter"
									>
										<FontAwesomeIcon icon={clearIcon} className="text-[10px]" />
									</button>
								</span>
							)}
						</div>
					</div>

					{/* Options list */}
					<div className="relative">
						{isSearching && (
							<div className="px-4 py-4 text-sm text-text-2">
								Searching...
							</div>
						)}
						{!isSearching && options.length === 0 && (
							<div className="px-4 py-4 text-sm text-text-2">
								No options found
							</div>
						)}
						{!isSearching && options.length > 0 && (
							<List
								ref={listRef}
								outerRef={listOuterRef}
								height={listHeight}
								itemCount={options.length}
								itemSize={LINE_HEIGHT}
								onScroll={handleScroll}
								className="scrollbar-thin"
							>
								{Row}
							</List>
						)}
						{/* Loading indicator - positioned as overlay at bottom of list */}
						{pagination.loading && options.length > 0 && (
							<div className="absolute bottom-0 left-0 right-0 px-4 py-2 text-xs text-text-2 text-center bg-gradient-to-t from-card via-card to-transparent">
								Loading more...
							</div>
						)}
					</div>
				</div>

				{/* Footer - sticky at bottom */}
				<div className="flex-shrink-0 border-t border-card-bd bg-card">
					{/* Include/Exclude mode selector */}
					<div className="px-4 py-3">
						<div className="flex items-center justify-center gap-2">
							<span className="text-sm text-text-2">Mode:</span>
							<div className="flex rounded-ctrl overflow-hidden border border-border">
								<button
									type="button"
									onClick={() => setFilterMode('include')}
									className={`px-3 py-1 text-xs cursor-pointer transition-colors ${
										filterMode === 'include'
											? 'bg-surface-2 text-text'
											: 'bg-card text-text-2 hover:bg-surface-2'
									}`}
								>
									Include
								</button>
								<button
									type="button"
									onClick={() => setFilterMode('exclude')}
									className={`px-3 py-1 text-xs cursor-pointer transition-colors ${
										filterMode === 'exclude'
											? 'bg-surface-2 text-text'
											: 'bg-card text-text-2 hover:bg-surface-2'
									}`}
									disabled
									title="Exclude mode coming soon"
								>
									Exclude
								</button>
							</div>
							<span className="text-xs text-text-2 italic">(coming soon)</span>
						</div>
					</div>

					{/* Remove filter button */}
					<div className="px-4 py-3 border-t border-card-bd">
						<div className="flex justify-end">
							<button
								type="button"
								onClick={handleRemoveFilter}
								className="px-3 py-1.5 text-sm text-danger hover:text-danger hover:bg-danger-soft rounded transition-colors cursor-pointer"
							>
								Remove filter
							</button>
						</div>
					</div>
				</div>
			</div>
			)}
		</div>
	);

	// Render via Portal to document.body to ensure fixed positioning is relative to viewport
	return createPortal(sidebarContent, document.body);
});

FilterSidebar.displayName = 'FilterSidebar';

FilterSidebar.propTypes = {
	/** Whether the sidebar is open */
	isOpen: PropTypes.bool.isRequired,
	/** Callback to close the sidebar */
	onClose: PropTypes.func.isRequired,
	/** The filter object to edit */
	filter: PropTypes.shape({
		category: PropTypes.string.isRequired,
		categoryDisplay: PropTypes.string,
		selectedValues: PropTypes.arrayOf(PropTypes.string),
	}),
	/** Callback to update filter selection - (category, selectedValues) */
	onUpdateFilter: PropTypes.func.isRequired,
	/** Callback to remove the filter - (category) */
	onRemoveFilter: PropTypes.func.isRequired,
	/** Auth token for API requests */
	token: PropTypes.string,
	/** Additional filters for API request */
	additionalFilters: PropTypes.array,
	/** Categories to exclude from API request */
	excludeCategories: PropTypes.array,
	/** Height of the header in pixels (sidebar starts below this) */
	headerHeight: PropTypes.number,
	/** Optional ref to the header element (alternative to data-header attribute) */
	headerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export default FilterSidebar;
